// src/components/Awards.tsx

'use client';

import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ExternalLink, Play } from "lucide-react";
import { Lightbox } from "@/components/Lightbox";
import awardsHeroImage from "@/assets/awards-hero.jpg";
import { useRecognitionsAPI } from "@/hooks/useRecognitionsAPI";
import { useGoogleReviews } from '@/hooks/useGoogleReviews';

type AwardItem = {
  id: string;
  image?: string;
  title: string;
  description: string;
  links: { text: string; url: string; icon: any }[];
};

export const Awards: React.FC = () => {
  const {
    categories,
    items, // already filtered by activeKey
    activeCategoryKey,
    setActiveCategoryKey,
    loading,
    error,
  } = useRecognitionsAPI();


  // Convert hook items (already filtered) to local render shape
  const filteredItems: AwardItem[] = useMemo(() => {
    return (items ?? []).map((it) => {
      const links =
        (it.links ?? []).map((l) => {
          const isVideo = /watch|video|trailer/i.test(l.label);
          return { text: l.label, url: l.href, icon: isVideo ? Play : ExternalLink };
        }) ?? [];
      return {
        id: it.id,
        image: it.imageUrl,
        title: it.title,
        description: it.description,
        links,
      };
    });
  }, [items]);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState<AwardItem[]>([]);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const handleCategoryChange = (key: string) => {
    if (key === activeCategoryKey) return;
    setActiveCategoryKey(key);
    setCurrentImageIndex(0);
  };

  const handleMainImageClick = () => {
    if (!filteredItems.length) return;
    setLightboxItems(filteredItems);
    setLightboxOpen(true);
  };

  const handlePrevious = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev > 0 ? prev - 1 : Math.max(filteredItems.length - 1, 0)
    );
  }, [filteredItems.length]);

  const handleNext = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev < filteredItems.length - 1 ? prev + 1 : 0
    );
  }, [filteredItems.length]);

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const onTouchEnd = () => {
    if (touchStart == null || touchEnd == null) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) handleNext();
    else if (distance < -minSwipeDistance) handlePrevious();
  };

  const currentItem = filteredItems[currentImageIndex];

  const lightboxImages = lightboxItems
    .filter((item) => item.image)
    .map((item) => ({
      filename: item.id,
      title: item.title,
      url: item.image!,
      width: 800,
      height: 600,
    }));

  // --- ClientReviewsCarousel (inside Awards, before return) ---
  const ClientReviewsCarousel: React.FC = () => {
    const { reviews } = useGoogleReviews(); // hook returns top 10 five-star
    const [startIndex, setStartIndex] = useState(0);
    const visibleCount = 3;

    // Keep startIndex within bounds when reviews change
    if (startIndex > Math.max((reviews?.length || 0) - visibleCount, 0)) {
      setTimeout(() => setStartIndex(Math.max((reviews?.length || 0) - visibleCount, 0)), 0);
    }

    const visible = (reviews || []).slice(startIndex, startIndex + visibleCount);

    const prev = () => setStartIndex((s) => Math.max(s - 1, 0));
    const next = () =>
      setStartIndex((s) => Math.min(s + 1, Math.max((reviews?.length || 0) - visibleCount, 0)));

    // Fallback: if no reviews, render original static placeholders
    if (!reviews || reviews.length === 0) {
      return (
        <div className="mt-6 relative max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="card-gradient border-none card-shadow">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => <span key={i}>★</span>)}
                  </div>
                </div>
                <p className="text-muted-foreground mb-4 italic">
                  "Absolutely stunning work! Seema created the most beautiful wedding cake that exceeded
                  all our expectations. Every detail was perfect and it tasted amazing too!"
                </p>
                <div className="text-sm">
                  <p className="font-semibold text-primary">Sarah & Michael</p>
                  <p className="text-muted-foreground">Wedding Cake</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card-gradient border-none card-shadow">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => <span key={i}>★</span>)}
                  </div>
                </div>
                <p className="text-muted-foreground mb-4 italic">
                  "The custom birthday cake for my daughter was beyond incredible. Seema captured every detail
                  we discussed and made it even more magical than we imagined!"
                </p>
                <div className="text-sm">
                  <p className="font-semibold text-primary">Jennifer R.</p>
                  <p className="text-muted-foreground">Birthday Celebration</p>
                </div>
              </CardContent>
            </Card>

            <Card className="card-gradient border-none card-shadow">
              <CardContent className="p-6">
                <div className="flex items-center mb-4">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => <span key={i}>★</span>)}
                  </div>
                </div>
                <p className="text-muted-foreground mb-4 italic">
                  "Professional, creative, and absolutely delicious! The sculpted cake for our corporate event
                  was a huge hit. Highly recommend Frosted Cake Art!"
                </p>
                <div className="text-sm">
                  <p className="font-semibold text-primary">Mark T.</p>
                  <p className="text-muted-foreground">Corporate Event</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    // Normal flow: show fetched reviews (responsive grid)
    return (
      <div className="mt-6 relative max-w-6xl mx-auto">
        {/* mobile vertical prev */}
        <div className="flex justify-center md:hidden mb-4">
          <button
            onClick={prev}
            aria-label="Previous reviews"
            className="p-2 bg-black/30 text-white rounded-full hover:bg-black/50 focus:outline-none"
          >
            <ChevronUp className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visible.map((r: any, idx: number) => {
            const reviewerName = (r?.reviewer_name || r?.reviewer?.displayName || 'Anonymous') as string;
            const profilePhoto = r?.reviewer_profile_photo || r?.reviewer?.profilePhotoUrl || null;
            const comment = (r?.comment ?? r?.text ?? '') as string;
            const dateRaw = r?.create_time || r?.createTime || r?.createAt || null;
            const key = r?.id ?? r?.reviewId ?? `${reviewerName}-${startIndex}-${idx}`;

            return (
              <Card key={key} className="card-gradient border-none card-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start mb-4">
                    <div className="mr-3">
                      {profilePhoto ? (
                        <img
                          src={profilePhoto}
                          alt={reviewerName}
                          className="w-10 h-10 rounded-full object-cover border border-primary/20"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm text-muted-foreground">
                          {reviewerName ? reviewerName[0] : '?'}
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-primary">{reviewerName}</p>
                          <p className="text-muted-foreground text-xs">
                            {dateRaw ? (() => { try { return new Date(dateRaw).toLocaleDateString(); } catch { return ''; } })() : ''}
                          </p>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <div className="flex text-yellow-400">
                            {[...Array(5)].map((_, i2) => <span key={i2}>★</span>)}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 text-muted-foreground italic">
                        {comment ? `“${comment}”` : 'No comment provided'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* mobile vertical next */}
        <div className="flex justify-center md:hidden mt-4">
          <button
            onClick={next}
            aria-label="Next reviews"
            className="p-2 bg-black/30 text-white rounded-full hover:bg-black/50 focus:outline-none"
          >
            <ChevronDown className="h-5 w-5" />
          </button>
        </div>

        {/* desktop left/right absolute controls */}
        {reviews.length > visibleCount && (
          <>
            <button
              onClick={prev}
              aria-label="Previous reviews"
              className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 p-2 bg-black/30 text-white rounded-full hover:bg-black/50 focus:outline-none"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <button
              onClick={next}
              aria-label="Next reviews"
              className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 p-2 bg-black/30 text-white rounded-full hover:bg-black/50 focus:outline-none"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>
    );
  };
  // --- end ClientReviewsCarousel ---

  return (
    <section
      id="awards"
      className="relative pt-24 pb-0 bg-gradient-to-b from-background to-muted/30 text-foreground"
    >
      {/* Hero */}
      <div className="relative h-96 bg-cover bg-center overflow-hidden">
        <img src={awardsHeroImage} alt="Awards and Recognition Hero" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h2 className="mb-4 text-[var(--hero-h1)]">Recognitions & Testimonials</h2>
            <p className="subheading max-w-3xl mx-auto mb-4 text-[var(--hero-h2)]">
              CELEBRATING EXCELLENCE IN SUGAR ARTISTRY
            </p>
            <p className="text-lg max-w-3xl mx-auto text-[var(--hero-h2)]">
              Our dedication to craft has been recognized across multiple platforms and competitions.
            </p>
          </div>
        </div>
      </div>

      <div className="pt-16 pb-0 bg-gradient-to-b from-background to-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              Founded by Seema Acharya, Frosted Cake Art in Chagrin Falls, OH has been honored with national
              awards, television appearances, and features in leading publications. Each recognition is a testament
              to artistry, innovation, and dedication to crafting unforgettable cakes.
            </p>
          </div>

          {/* Tabs for Awards (shadcn pattern, like Gallery) */}
          <Tabs value={activeCategoryKey} onValueChange={handleCategoryChange}>
            <TabsList
              // CRITICAL FIX: Increased margin to prevent overlap on narrow screens
              className="grid w-full max-w-4xl mx-auto grid-cols-3 md:grid-cols-6 gap-2 bg-transparent p-0 shadow-none rounded-none mb-20" 
              style={
                {
                  // Awards-only CSS vars (runtime writes these)
                  ['--tab-active-bg' as any]: 'var(--awards-tab-active-bg)',
                  ['--tab-active-text' as any]: 'var(--awards-tab-active-text)',
                  ['--tab-inactive-bg' as any]: 'var(--awards-tab-inactive-bg)',
                  ['--tab-inactive-text' as any]: 'var(--awards-tab-inactive-text)',
                } as React.CSSProperties
              }
            >
              {categories.map((c) => (
                <TabsTrigger
                  key={c.key}
                  value={c.key}
                  className={[
                    "px-4 py-3 text-sm font-medium rounded-[6px] border-0 shadow-none transition",
                    // Mirror Gallery: rely on data-state from shadcn TabsTrigger
                    "data-[state=active]:bg-[var(--tab-active-bg)] data-[state=active]:text-[var(--tab-active-text)]",
                    "data-[state=inactive]:bg-[var(--tab-inactive-bg)] data-[state=inactive]:text-[var(--tab-inactive-text)]",
                  ].join(" ")}
                >
                  {c.label === "International Competitions" ? "Int'l Competitions" : c.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          
          {/* Loading / Error */}
          {loading && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Loading recognitions…</p>
            </div>
          )}
          {error && (
            <div className="text-center py-12">
              <p className="text-red-600">Error: {error}</p>
            </div>
          )}

          {/* Main content */}
          {!loading && !error && (
            <>
              {filteredItems.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground mb-2">
                    No awards yet in{" "}
                    {activeCategoryKey === "all" ? "this collection" : `the ${activeCategoryKey} category`}.
                  </p>
                  <p className="text-sm text-muted-foreground">Check back soon for new recognitions!</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Two-column: image + content */}
                  <div className="grid lg:grid-cols-2 gap-8 items-center max-w-6xl mx-auto">
                    {/* Left: Image with swipe + arrows */}
                    <div className="relative">
                      <button
                        onClick={handleMainImageClick}
                        className="group w-full overflow-hidden rounded-2xl card-shadow transition hover:scale-[1.02] focus:ring-2 focus:ring-primary focus:outline-none"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                      >
                        <div className="aspect-[4/3] bg-muted">
                          <img
                            src={currentItem?.image}
                            alt={currentItem?.title}
                            className="w-full h-full object-cover transition group-hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                      </button>

                      {filteredItems.length > 1 && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={handlePrevious}
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 focus:ring-2 focus:ring-white bg-black/30 backdrop-blur-sm opacity-80 hover:opacity-100"
                            aria-label="Previous award"
                          >
                            <ChevronLeft className="h-6 w-6" />
                          </Button>

                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleNext}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 focus:ring-2 focus:ring-white bg-black/30 backdrop-blur-sm opacity-80 hover:opacity-100"
                            aria-label="Next award"
                          >
                            <ChevronRight className="h-6 w-6" />
                          </Button>
                        </>
                      )}
                    </div>

                    {/* Right: title/desc/links */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-2xl font-heading font-semibold text-primary mb-4">
                          {currentItem?.title}
                        </h3>
                        <p className="text-muted-foreground leading-relaxed text-lg">
                          {currentItem?.description}
                        </p>
                      </div>

                      {!!currentItem?.links?.length && (
                        <div className="flex flex-wrap gap-3">
                          {currentItem.links.map((link, i) => (
                            <Button key={i} variant="outline" size="sm" asChild>
                              <a href={link.url} target="_blank" rel="noopener noreferrer">
                                <link.icon className="h-4 w-4 mr-2" />
                                {link.text}
                              </a>
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Thumbnails */}
                  <div className="flex gap-4 justify-center overflow-x-auto pb-4 px-4 sm:px-0">
                    {filteredItems.map((item, idx) => (
                      <button
                        key={item.id}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={[
                          "flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden transition focus:ring-2 focus:ring-primary focus:outline-none",
                          idx === currentImageIndex ? "ring-2 ring-primary scale-105" : "opacity-70 hover:opacity-100"
                        ].join(" ")}
                      >
                        <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Client Reviews (Google-driven) */}
          <div className="mt-24 pt-12 border-t border-border/20">
            <div className="text-center mb-6">
              <h3 className="text-3xl font-heading font-semibold text-primary mb-4">Client Reviews</h3>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Hear what our clients say about their experience with Frosted Cake Art
              </p>
            </div>

            <ClientReviewsCarousel />
          </div>

          {/* Google Reviews Button */}
          <div className="mt-12 mb-24 text-center">
            <Button
              asChild
              size="lg"
              className="w-full max-w-md mx-auto text-lg font-semibold"
            >
              <a
                href="https://www.google.com/search?q=Frosted+Cake+Art+Reviews"
                target="_blank"
                rel="noopener noreferrer"
              >
                ⭐ Read More Reviews on Google ⭐
              </a>
            </Button>
          </div>

          {/* Spacer section to match About → Gallery transition (invisible) */}
          <div className="py-6" />

          {/* Lightbox (only when there are images) */}
          {lightboxOpen && lightboxImages.length > 0 && (
            <Lightbox
              images={lightboxImages}
              currentIndex={currentImageIndex}
              isOpen={lightboxOpen}
              onClose={() => setLightboxOpen(false)}
              onNavigate={(index) => setCurrentImageIndex(index)}
            />
          )}
        </div> {/* end .max-w-7xl */}
      </div> {/* end .py-24 */}
    </section>
  );
};

export default Awards;